import type { Messages } from './types'

export const de: Messages = {
  meta: {
    language: 'Deutsch',
    htmlLang: 'de',
  },

  site: {
    name: 'Mazzella Rent',
    tagline: 'Auf Ischia seit 1965',
    skipToContent: 'Zum Inhalt springen',
    languageSwitcher: 'Sprache der Website',
    sampleBanner:
      'Testdaten: Fahrzeuge, Preise und Verfügbarkeit sind Beispieldaten. Hier gemachte Buchungen sind nicht echt.',
    footerAddress: 'Fährterminal und Tragflügelboot-Terminal, Ischia Porto',
    bookingTitle: 'Auto, Roller oder E-Bike auf Ischia buchen',
    bookingDescription:
      'Auto-, Roller- und E-Bike-Vermietung auf Ischia: aktuelle Preise und Verfügbarkeit, Abholung am Hafen oder Lieferung ins Hotel.',
  },

  common: {
    back: 'Zurück',
    continue: 'Weiter',
    edit: 'Ändern',
    retry: 'Erneut versuchen',
    loading: 'Wird geladen…',
    optional: 'optional',
    total: 'Gesamt',
    select: 'Auswählen',
    yes: 'Ja',
    no: 'Nein',
    days: { one: '{count} Tag', other: '{count} Tage' },
    perDay: '{amount} pro Tag',
    perRental: '{amount} pro Miete',
    steps: 'Schritt {current} von {total}',
    stepNames: { search: 'Suche', results: 'Fahrzeug', options: 'Extras', details: 'Daten' },
    vehicle: 'Fahrzeug',
  },

  legs: {
    pickup: 'Abholung',
    return: 'Rückgabe',
  },

  search: {
    title: 'Fahrzeug buchen',
    intro:
      'Autos, Roller und E-Bikes auf Ischia. Sagen Sie uns, wo und wann – wir zeigen Ihnen sofort Preise und Verfügbarkeit.',
    pickupPlace: 'Abholort',
    returnPlace: 'Rückgabeort',
    sameAsPickup: 'Wie Abholort',
    officesGroup: 'Unsere Büros',
    deliveryGroup: 'Lieferung',
    deliveryFee: '{name} (Lieferung {amount})',
    pickupDate: 'Abholdatum',
    pickupTime: 'Abholzeit',
    returnDate: 'Rückgabedatum',
    returnTime: 'Rückgabezeit',
    rentalDay: 'Ein Miettag dauert von {time} Uhr bis {time} Uhr am nächsten Tag.',
    closed: 'An diesem Tag geschlossen',
    noTimesLeft: 'Heute keine Uhrzeiten mehr',
    promoToggle: 'Haben Sie einen Aktionscode?',
    promoCode: 'Aktionscode',
    submit: 'Verfügbarkeit prüfen',
    returnBeforePickup: 'Die Rückgabe muss nach der Abholung liegen.',
  },

  results: {
    title: 'Fahrzeug wählen',
    period: '{from} → {to}',
    choose: 'Wählen',
    unavailable: 'Für diese Daten ausgebucht',
    notBookable: 'Für diese Daten nicht buchbar',
    none: 'Für diese Daten ist kein Fahrzeug verfügbar. Versuchen Sie andere Daten oder einen anderen Ort.',
    editSearch: 'Suche ändern',
    promoApplied: 'Code {code} angewendet.',
    promoNotApplied: 'Code {code} nicht angewendet: {reason}',
    promoBlocks:
      'Dieser Code gilt nicht für diese Suche: Ändern Sie Daten oder Ort, oder suchen Sie ohne Code.',
    searchWithoutCode: 'Ohne Code suchen',
  },

  options: {
    title: 'Extras und Versicherung',
    extras: 'Extras',
    noExtras: 'Für dieses Fahrzeug gibt es keine Extras.',
    quantity: 'Anzahl {name}',
    coverages: 'Versicherung',
    included: 'Inklusive',
    excess: 'Selbstbeteiligung {amount}',
    summary: 'Ihre Miete',
    updating: 'Preis wird aktualisiert…',
  },

  details: {
    title: 'Ihre Daten',
    driver: 'Fahrer',
    driverNote: 'Fahren darf nur, wer den Mietvertrag unterschreibt.',
    firstName: 'Vorname',
    lastName: 'Nachname',
    email: 'E-Mail',
    phone: 'Telefon',
    phoneHint: 'Mit internationaler Vorwahl, zum Beispiel +49 151 23456789.',
    birthDate: 'Geburtsdatum',
    licence: 'Führerschein',
    licenceAccepted: 'Für dieses Fahrzeug akzeptierte Führerscheine: {categories}.',
    licenceYears: {
      one: 'Sie müssen die Klasse seit mindestens {count} Jahr besitzen.',
      other: 'Sie müssen die Klasse seit mindestens {count} Jahren besitzen.',
    },
    licenceNumber: 'Führerscheinnummer',
    issuingCountry: 'Ausstellungsland',
    licenceCategory: 'Klasse',
    firstIssuedOn: 'Datum, an dem Sie die Klasse erworben haben',
    firstIssuedHint: 'Es ist das Datum in Feld 10 eines EU-Führerscheins.',
    expiresOn: 'Ablaufdatum',
    hasIdp: 'Ich habe auch einen Internationalen Führerschein (IDP)',
    notes: 'Hinweise für das Büro',
    termsBefore: 'Ich habe die folgenden Bedingungen gelesen und akzeptiere sie:',
    termsLink: 'Mietbedingungen',
    termsVersion: 'Version {version}',
    privacyBefore: 'Wie wir Ihre Daten verwenden:',
    privacyLink: 'Datenschutzerklärung',
    paymentTitle: 'Zahlung bei Abholung',
    paymentReadyTitle: 'Zahlung',
    paymentReady:
      'Nach der Bestätigung können Sie sofort online auf einer sicheren Seite von Stripe bezahlen oder bei der Abholung bar oder mit Karte.',
    paymentPending:
      'Online-Zahlung ist noch nicht verfügbar: Sie zahlen bei der Abholung im Büro, bar oder mit Karte.',
    deposit: 'Bei der Abholung ist eine Kaution per Kredit- oder Debitkarte erforderlich.',
    submit: 'Buchung bestätigen',
    submitting: 'Wird gesendet…',
    pendingNotice:
      'Wir wissen nicht, ob Ihre letzte Anfrage angekommen ist. Wir prüfen sie zuerst, damit keine zweite Buchung entsteht.',
    checkPending: 'Frühere Anfrage prüfen',
    priceChanged:
      'Der Preis hat sich geändert: Der neue Gesamtpreis beträgt {amount}. Bitte prüfen und erneut bestätigen.',
  },

  done: {
    title: 'Buchung bestätigt',
    titleEnded: 'Ihre Buchung',
    code: 'Buchungscode',
    codeHint: 'Bewahren Sie diesen Code auf: Sie brauchen ihn bei der Abholung.',
    status: 'Status',
    payAtCounter: 'Sie zahlen {amount} bei der Abholung, bar oder mit Karte.',
    counterOnly: 'Sie zahlen {amount} an unserem Schalter, bar oder mit Karte.',
    checks: 'Bei der Abholung prüfen wir:',
    documents: 'Dokumente',
    documentsIntro:
      'Optional: Laden Sie jetzt Ausweis und Führerschein hoch, dann geht die Abholung schneller.',
    documentKind: 'Dokument',
    documentSide: 'Seite',
    file: 'Datei auswählen',
    noFile: 'Keine Datei ausgewählt',
    fileHint: 'JPG, PNG oder PDF, höchstens {size}.',
    upload: 'Hochladen',
    uploading: 'Wird hochgeladen…',
    uploaded: '{document}: hochgeladen',
    newBooking: 'Neue Buchung',
    restoreFailed: 'Wir können Ihre Buchung gerade nicht neu laden.',
    payTitle: 'Zahlung',
    payIntro: 'Sie können jetzt online bezahlen oder bei der Abholung bar oder mit Karte.',
    payOnline: 'Jetzt {amount} bezahlen',
    payRedirecting: 'Wir leiten Sie zur Zahlungsseite weiter…',
    payHint: 'Sie bezahlen auf einer sicheren Seite von Stripe und kehren danach hierher zurück.',
    paySample:
      'Eine Testzahlung: Es wird nichts abgebucht. Verwenden Sie die Karte 4242 4242 4242 4242, ein beliebiges künftiges Datum und eine beliebige Prüfnummer.',
    ended:
      'Diese Buchung ist nicht mehr aktiv: Online wird nichts bezahlt, und es werden keine Dokumente benötigt. Bei Fragen zu Zahlungen oder Erstattungen kontaktieren Sie uns.',
    paidTitle: 'Zahlung erhalten',
    paidAmount: 'Bezahlt: {amount}.',
    balanceDue: 'Noch zu zahlen: {amount}.',
    paymentConfirming: 'Wir bestätigen Ihre Zahlung…',
    paymentSlow:
      'Die Bestätigung dauert länger als üblich. Wenn Sie bezahlt haben, erscheint sie hier in Kürze: Bitte zahlen Sie nicht doppelt.',
    paymentProcessing:
      'Ihre Bank bestätigt die Zahlung noch. Sie müssen nichts weiter tun: Sie erscheint hier.',
    paymentCancelled:
      'Die Zahlung wurde nicht abgeschlossen. Sie können es erneut versuchen oder bei der Abholung bezahlen.',
    paymentFailed:
      'Die Zahlung ist nicht durchgegangen. Sie können es erneut versuchen oder bei der Abholung bezahlen.',
    paymentNoBooking:
      'Sie sind von der Zahlungsseite zurück, aber dieses Fenster kennt Ihre Buchung nicht mehr. Wenn Sie bezahlt haben, ist die Zahlung erfasst und das Büro sieht sie bei der Abholung.',
    payLinkInvalid:
      'Dieser Zahlungslink ist abgelaufen oder ungültig. Bitten Sie Mazzella Rent um einen neuen oder bezahlen Sie bei der Abholung.',
  },

  validation: {
    required: 'Pflichtfeld.',
    email: 'Bitte prüfen Sie die E-Mail-Adresse.',
    phone: 'Geben Sie die Nummer mit internationaler Vorwahl ein, zum Beispiel +49 151 23456789.',
    date: 'Wählen Sie ein gültiges Datum.',
    pastDate: 'Das Datum muss in der Vergangenheit liegen.',
    terms: 'Um zu buchen, müssen Sie die Mietbedingungen akzeptieren.',
    fileType: 'Format nicht unterstützt: Verwenden Sie JPG, PNG oder PDF.',
    fileSize: 'Die Datei ist zu groß: höchstens {size}.',
  },

  vehicleKinds: {
    car: 'Auto',
    van: 'Transporter',
    minivan: 'Minivan',
    scooter: 'Roller',
    motorcycle: 'Motorrad',
    ebike: 'E-Bike',
  },

  quoteLines: {
    rental: 'Miete',
    surcharge: 'Zuschlag',
    discount: 'Rabatt {code}',
    delivery: 'Lieferung: {name}',
    collection: 'Abholung am Ende: {name}',
    one_way: 'Rückgabe an einem anderen Ort',
    extra: '{name}',
    coverage: '{name}',
  },

  youngDriver: 'Zuschlag für junge Fahrer',

  refusals: {
    invalid_period: 'Die Rückgabe muss nach der Abholung liegen.',
    in_the_past: 'Dieser Abholzeitpunkt liegt in der Vergangenheit: Wählen Sie ein späteres Datum.',
    category_unavailable: 'Dieses Fahrzeug kann nicht online gebucht werden.',
    season_not_configured: 'Vom {from} bis {to} haben wir noch keine Preise.',
    rate_not_configured: 'Für diese Mietdauer haben wir noch keinen Preis.',
    day_price_not_configured: 'Vom {from} bis {to} haben wir noch keine Preise.',
    stop_sale:
      'Vom {from} bis {to} nehmen wir keine Online-Buchungen an: rufen Sie uns an oder schreiben Sie uns.',
    min_stay: 'Für diese Daten beträgt die Mindestmietdauer {minDays} Tage.',
    place_unknown: 'Dieser Ort ist nicht mehr verfügbar.',
    outside_opening_hours: '{leg} um {time}: Das Büro {location} ist zu dieser Zeit geschlossen.',
    delivery_blackout: '{leg} · {zone}: in diesem Zeitraum nicht verfügbar.',
    delivery_lead_time: '{leg} · {zone}: Wir brauchen mindestens {minutes} Minuten Vorlauf.',
    delivery_cutoff: '{leg} · {zone}: Bitte bis {cutoff} Uhr am Vortag buchen.',
    delivery_fee_not_configured: '{leg} · {zone}: online nicht buchbar.',
    one_way_fee_not_configured: 'Die Rückgabe an einem anderen Ort ist online nicht verfügbar.',
    extra_unknown: 'Ein gewähltes Extra ist nicht mehr verfügbar.',
    extra_not_for_category: '{extra} ist für dieses Fahrzeug nicht verfügbar.',
    extra_quantity: '{extra}: höchstens {max} pro Buchung.',
    extra_out_of_stock: '{extra}: Für diese Daten sind nur noch {left} verfügbar.',
    coverage_unknown: 'Eine gewählte Versicherung ist nicht mehr verfügbar.',
    driver_too_young: 'Für dieses Fahrzeug muss der Fahrer mindestens {minAge} Jahre alt sein.',
    young_driver_surcharge_not_configured:
      'Für einen Fahrer in Ihrem Alter ist dieses Fahrzeug nur im Büro buchbar.',
  },

  promoRefusals: {
    unknown: 'Der Code existiert nicht.',
    inactive: 'Der Code ist nicht aktiv.',
    not_yet_valid: 'Der Code ist noch nicht gültig.',
    expired: 'Der Code ist abgelaufen.',
    exhausted: 'Der Code ist aufgebraucht.',
    blackout: 'Der Code gilt nicht für diese Daten.',
    location: 'Der Code gilt nur für bestimmte Abhol- und Rückgabeorte.',
  },

  eligibility: {
    rules_not_configured: 'die Anforderungen an den Fahrer für dieses Fahrzeug.',
    birth_date_missing: 'Ihr Geburtsdatum.',
    too_young: 'das Mindestalter: Sie müssen mindestens {minAge} Jahre alt sein.',
    licence_rules_not_configured: 'den für dieses Fahrzeug nötigen Führerschein.',
    licence_missing: 'Ihren Führerschein.',
    licence_expired: 'das Ablaufdatum des Führerscheins ({expiresOn}).',
    licence_category: 'die Führerscheinklasse: Wir akzeptieren {accepted}.',
    licence_first_issue_unknown: 'wie lange Sie den Führerschein schon besitzen.',
    licence_too_recent: 'wie lange Sie den Führerschein besitzen: mindestens {minYears} Jahre.',
    idp_required:
      'den Internationalen Führerschein, nötig für Führerscheine aus diesem Land: {country}.',
    manual_check: 'Ihren Führerschein, wie bei jeder Miete dieses Fahrzeugs.',
  },

  documentKinds: {
    id_card: 'Personalausweis',
    passport: 'Reisepass',
    driving_licence: 'Führerschein',
    other: 'Anderes Dokument',
  },

  documentSides: {
    front: 'Vorderseite',
    back: 'Rückseite',
    single: 'Einzelne Seite',
  },

  reservationStatuses: {
    draft: 'Ausstehend',
    confirmed: 'Bestätigt',
    checked_out: 'Laufend',
    returned: 'Zurückgegeben',
    closed: 'Abgeschlossen',
    cancelled: 'Storniert',
    no_show: 'Nicht abgeholt',
  },

  payment: {
    /** The line Stripe's page shows. */
    item: 'Mietwagen Mazzella Rent, Buchung {code}',
    itemSample: 'Testzahlung: Buchung {code}',
  },

  emails: {
    greeting: 'Hallo {name},',
    signature: 'Mazzella Rent, Ischia seit 1965',
    sample: 'Testbuchung: Es ist kein Fahrzeug wirklich reserviert.',
    automatic:
      'Diese E-Mail wird automatisch versendet: Bitte antworten Sie nicht an diese Adresse.',
    call: 'Für Änderungen rufen Sie uns an: {phone}.',
    pickupReminder: {
      subject: 'Erinnerung: Abholung {when} · Buchung {code}',
      intro: 'wir erinnern Sie an die Abholung Ihrer Buchung.',
      ferry: 'Fähre',
    },
    returnReminder: {
      subject: 'Erinnerung: Rückgabe {when} · Miete {code}',
      intro: 'wir erinnern Sie an die Rückgabe des Fahrzeugs.',
      due: 'Es sind noch {amount} zu zahlen: Sie zahlen bei der Rückgabe, bar oder mit Karte.',
    },
    questions: 'Bei Fragen rufen Sie uns an: {phone}.',
    /** Q-97 (S7): a cancellation, facts only: no clause is quoted. */
    bookingCancelled: {
      subject: 'Buchung {code} storniert',
      intro: 'Ihre Buchung wurde storniert.',
      paid: 'Bezahlt',
      fee: 'Stornogebühr',
      returned: 'Bereits erstattet',
      toReturn: 'Zu erstatten',
      refundFollows: 'Wir schreiben Ihnen erneut, sobald die Erstattung veranlasst wurde.',
    },
    /** W2-S S4: a website booking's hold let go, its online share not paid in time. */
    holdReleased: {
      subject: 'Buchung {code} nicht abgeschlossen',
      intro:
        'die Online-Zahlung für Ihre Buchung wurde nicht rechtzeitig abgeschlossen. Das Fahrzeug ist daher nicht mehr für Sie reserviert, und es wurde nichts belastet.',
      bookAgain: 'Hier können Sie erneut buchen: {url}',
    },
    /** Q-97 (S7): a refund sent; "sent", never "received": a card refund can still fail. */
    refundSent: {
      subject: 'Buchung {code}: Erstattung von {amount}',
      intro: 'wir haben Ihnen eine Erstattung veranlasst.',
      amount: 'Betrag',
      method: 'Art',
      methods: {
        stripe: 'Auf die für die Zahlung verwendete Karte',
        card_terminal: 'Auf Ihre Karte, an unserem Terminal',
        bank_transfer: 'Per Überweisung',
        cash: 'Bar, am Schalter',
      },
      timing: {
        stripe: 'Sie erscheint in der Regel innerhalb von 5–10 Werktagen auf Ihrem Kontoauszug.',
        card_terminal: 'Wann sie erscheint, hängt von der Bank Ihrer Karte ab.',
        bank_transfer: 'Wann sie eingeht, hängt von den Banken ab.',
        cash: 'Sie wurde Ihnen am Schalter ausgehändigt.',
      },
    },
    paymentLink: {
      subject: 'Buchung {code}: Ihr Zahlungslink',
      intro: 'hier ist der Link, um Ihre Buchung online zu bezahlen.',
      introFineFee:
        'hier ist der Link, um den offenen Betrag Ihrer Anmietung online zu bezahlen: die Bearbeitung eines Bußgeldbescheids zur Anmietung.',
      balance: 'Zu zahlen',
      pay: 'Bezahlen Sie sicher mit Karte auf der Seite von Stripe: {url}',
      expires: 'Der Link ist gültig bis {date}.',
      ignore:
        'Wenn Sie bereits bezahlt oder diese Buchung nicht vorgenommen haben, ignorieren Sie diese E-Mail: Der Link zeigt nur den zu zahlenden Betrag.',
    },
    contractSigned: {
      subject: 'Miete {code}: Ihr unterschriebener Vertrag',
      intro: 'im Anhang finden Sie den Mietvertrag, den Sie am Schalter unterschrieben haben.',
      language: 'Der Vertrag ist auf Italienisch.',
      keep: 'Bitte bewahren Sie ihn für die gesamte Mietdauer auf.',
    },
    bookingConfirmed: {
      subject: 'Buchung {code} bestätigt',
      intro: 'Ihre Buchung ist bestätigt.',
      code: 'Buchungscode',
      keepCode: 'Bewahren Sie diesen Code auf: Sie brauchen ihn bei der Abholung.',
      pickup: 'Abholung',
      return: 'Rückgabe',
      vehicle: 'Fahrzeug',
      total: 'Gesamt',
      payAtCounter: 'Sie zahlen {amount} bei der Abholung, bar oder mit Karte.',
      payUnlessPaidOnline:
        'Falls Sie nicht bereits online bezahlt haben, zahlen Sie {amount} bei der Abholung, bar oder mit Karte.',
      partlyPaid:
        'Bisher bezahlt: {paid}. Den Rest, {balance}, zahlen Sie bei der Abholung, bar oder mit Karte.',
      paid: 'Zahlung erhalten: {amount}. Vielen Dank!',
      bringLicence: 'Bringen Sie zur Abholung einen Ausweis und Ihren Führerschein mit.',
      bringId: 'Bringen Sie zur Abholung einen Ausweis mit.',
    },
    rentalReturned: {
      subject: 'Miete {code}: Übersicht',
      intro: 'vielen Dank für Ihre Miete. Hier ist Ihre Übersicht.',
      code: 'Buchungscode',
      vehicle: 'Fahrzeug',
      returned: 'Rückgabe',
      km: 'Gefahrene Kilometer',
      rental: 'Miete',
      paid: 'Bezahlt',
      balance: 'Noch zu zahlen',
      settled: 'Alles bezahlt. Vielen Dank und bis bald!',
      due: 'Noch zu zahlen: {amount}.',
      notReceipt: 'Diese Übersicht ist kein Steuerbeleg.',
      chargeKinds: {
        fuel: 'Kraftstoff',
        late: 'Verspätete Rückgabe',
        cleaning: 'Reinigung',
        damage: 'Schäden',
        other: 'Sonstiges',
        fine_fee: 'Bearbeitung eines Bußgeldbescheids zur Anmietung',
        excess_km: 'Zusätzliche Kilometer',
      },
    },
  },

  signing: {
    title: 'Ihr Mietvertrag',
    intro: 'Lesen Sie den Vertrag und unterschreiben Sie unten in beiden Feldern.',
    language:
      'Der Vertrag und die Bedingungen sind auf Italienisch. Wenn etwas unklar ist, fragen Sie das Personal.',
    draft: 'ENTWURF: Testbedingungen, ohne rechtliche Wirkung.',
    signMain: 'Hier unterschreiben, um den Vertrag und die Bedingungen anzunehmen',
    signApproval: 'Hier unterschreiben, um die oben genannten Punkte ausdrücklich zu genehmigen',
    pad: 'Unterschriftsfeld: mit dem Finger oder dem Stift unterschreiben.',
    padEmpty: 'Keine Unterschrift',
    padSigned: 'Unterschrieben',
    clear: 'Unterschrift löschen',
    submit: 'Vertrag unterschreiben',
    submitHint: 'Unterschreiben Sie in beiden Feldern, um fortzufahren.',
    signedTitle: 'Danke, der Vertrag ist unterschrieben',
    signedBody: 'Bitte geben Sie das Gerät dem Personal zurück.',
    signedEmail: 'Sie erhalten eine Kopie des Vertrags per E-Mail.',
    done: 'Gerät zurückgeben',
    cancel: 'Jetzt nicht unterschreiben',
    expires: 'Diese Ansicht schließt um {time}.',
    /** C7: the contract could not be read; said in it, en and de, the booking's language unknown. */
    unavailable:
      'Der Vertrag lässt sich auf diesem Gerät nicht öffnen. Bitte geben Sie es dem Personal zurück.',
  },

  contract: {
    title: 'Mietvertrag',
    booking: 'Buchung {code}',
    draft: 'ENTWURF',
    draftNotice: 'Entwurf: Testbedingungen, ohne rechtliche Wirkung.',
    sections: {
      company: 'Vermieter',
      customer: 'Kunde',
      licence: 'Führerschein',
      secondDriver: 'Zweiter Fahrer',
      vehicle: 'Fahrzeug',
      period: 'Mietzeitraum',
      prices: 'Preise',
      cover: 'Versicherungsschutz',
      deposit: 'Kaution',
      fees: 'Vertragsstrafen und Gebühren',
      approval: 'Ausdrückliche Genehmigung (Art. 1341 und 1342 italienisches Zivilgesetzbuch)',
      signatures: 'Unterschriften',
    },
    fields: {
      name: 'Name',
      address: 'Adresse',
      vatNumber: 'USt-IdNr.',
      birthDate: 'Geburtsdatum',
      birthPlace: 'Geburtsort',
      nationality: 'Staatsangehörigkeit',
      email: 'E-Mail',
      phone: 'Telefon',
      licenceNumber: 'Nummer',
      issuingCountry: 'Ausstellungsland',
      expiresOn: 'Gültig bis',
      categories: 'Klassen',
      plate: 'Kennzeichen',
      model: 'Modell',
      category: 'Kategorie',
      from: 'Abholung',
      to: 'Rückgabe',
      total: 'Gesamt',
      excess: 'Selbstbeteiligung',
    },
    notConfigured: 'noch nicht hinterlegt',
    noLicence: 'Kein Führerschein hinterlegt',
    noCover: 'Nur Haftpflicht (RCA)',
    noExcess: 'keine',
    depositPending: 'Noch offen: Betrag und Art sind noch nicht festgelegt.',
    depositAmount: '{amount}, Art noch offen',
    adjustment: 'Am Schalter vereinbarte Änderung',
    secondDriverFee: 'Zweiter Fahrer',
    fees: {
      fuel_refill: 'Nicht aufgetankt',
      one_way: 'Rückgabe an einem anderen Ort',
      fine_admin: 'Bearbeitung eines Bußgeldbescheids zur Anmietung',
    },
    signatureMain: 'Unterschrift des Kunden',
    signatureApproval: 'Unterschrift zur ausdrücklichen Genehmigung',
    signedAt: 'Unterschrieben am {date}',
    fingerprint: 'SHA-256-Fingerabdruck des Vertrags: {hash}',
    termsFingerprint: 'Bedingungen {version}, SHA-256-Fingerabdruck: {hash}',
  },

  errors: {
    network:
      'Der Dienst ist nicht erreichbar. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    fallback: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es gleich noch einmal.',
    codes: {
      validation_failed:
        'Einige Angaben sind ungültig. Prüfen Sie die Felder und versuchen Sie es erneut.',
      bad_request: 'Die Anfrage ist ungültig. Laden Sie die Seite neu und versuchen Sie es erneut.',
      not_found: 'Wir konnten nicht finden, wonach Sie suchen.',
      internal_error:
        'Bei uns ist etwas schiefgelaufen. Bitte versuchen Sie es gleich noch einmal.',
      rate_limited:
        'Zu viele Anfragen in kurzer Zeit. Versuchen Sie es in {retryAfterSeconds} Sekunden erneut.',
      app_version_required: 'Laden Sie die Seite neu und versuchen Sie es erneut.',
      app_version_unsupported: 'Eine neue Version ist verfügbar. Bitte laden Sie die Seite neu.',
      not_configured: 'Die Online-Buchung ist für diese Daten noch nicht eingerichtet.',
      category_not_found: 'Dieses Fahrzeug ist nicht mehr verfügbar.',
      category_inactive: 'Dieses Fahrzeug ist nicht mehr verfügbar.',
      not_available: 'Leider ist das Fahrzeug für diese Daten nicht mehr verfügbar.',
      quote_refused: 'Für diese Auswahl können wir keinen Preis berechnen.',
      promo_refused: 'Der Aktionscode gilt nicht für diese Buchung.',
      not_eligible: 'Der Fahrer erfüllt die Anforderungen für dieses Fahrzeug nicht.',
      price_changed:
        'Der Preis hat sich geändert. Prüfen Sie den neuen Gesamtpreis und bestätigen Sie erneut.',
      terms_changed:
        'Die Mietbedingungen haben sich geändert. Lesen Sie sie und akzeptieren Sie sie erneut.',
      terms_draft:
        'Unterschreiben und Buchen ist gerade nicht möglich: Die Mietbedingungen sind nicht verfügbar. Bitte wenden Sie sich an uns.',
      idempotency_key_required: 'Laden Sie die Seite neu und versuchen Sie es erneut.',
      idempotency_key_invalid: 'Laden Sie die Seite neu und versuchen Sie es erneut.',
      idempotency_key_reused:
        'Diese Anfrage wurde bereits mit anderen Angaben gesendet. Laden Sie die Seite neu.',
      idempotency_key_expired:
        'Diese Anfrage ist zu alt, um wiederholt zu werden. Laden Sie die Seite neu und prüfen Sie, bevor Sie es erneut versuchen.',
      booking_not_found: 'Keine Buchung passt zu diesen Angaben.',
      booking_token_invalid: 'Dieser Link ist abgelaufen. Suchen Sie Ihre Buchung erneut.',
      upload_link_invalid: 'Der Upload-Link ist abgelaufen. Versuchen Sie es erneut.',
      already_uploaded: 'Diese Datei wurde bereits hochgeladen.',
      booking_closed:
        'Diese Buchung ist nicht mehr aktiv: Es können keine Dokumente hinzugefügt werden.',
      file_too_large: 'Die Datei ist zu groß.',
      unsupported_content_type: 'Format nicht unterstützt: Verwenden Sie JPG, PNG oder PDF.',
      wrong_content_type: 'Die Datei passt nicht zum angegebenen Format. Versuchen Sie es erneut.',
      content_mismatch: 'Der Inhalt der Datei passt nicht zu ihrem Format.',
      too_many_documents: 'Sie haben bereits die Höchstzahl an Dokumenten hochgeladen.',
      vehicle_not_in_category: 'Dieses Fahrzeug ist nicht mehr verfügbar.',
      hold_required: 'Bei uns ist etwas schiefgelaufen. Bitte versuchen Sie es gleich noch einmal.',
      illegal_transition: 'Diese Buchung kann nicht mehr geändert werden.',
      version_conflict: 'Die Angaben haben sich inzwischen geändert. Laden Sie die Seite neu.',
      invalid_period: 'Die Rückgabe muss nach der Abholung liegen.',
      invalid_date: 'Wählen Sie ein gültiges Datum.',
      invalid_time: 'Wählen Sie eine gültige Uhrzeit.',
      invalid_amount:
        'Bei uns ist etwas schiefgelaufen. Bitte versuchen Sie es gleich noch einmal.',
      invalid_quantity: 'Prüfen Sie die gewählten Mengen.',
      invalid_rate: 'Bei uns ist etwas schiefgelaufen. Bitte versuchen Sie es gleich noch einmal.',
      nothing_to_pay: 'Für diese Buchung ist nichts zu bezahlen.',
      booking_not_payable: 'Diese Buchung kann nicht online bezahlt werden.',
      booking_disputed: 'Diese Buchung wird an unserem Schalter beglichen, bar oder mit Karte.',
      payment_processing:
        'Eine Zahlung für diese Buchung wird gerade bestätigt. Bitte warten Sie einige Minuten, bevor Sie es erneut versuchen.',
      online_payment_pending:
        'Die Online-Zahlung ist noch nicht verfügbar: Sie bezahlen bei der Abholung.',
      hold_ended:
        'Die Zeit für die Online-Zahlung ist abgelaufen und das Fahrzeug ist nicht mehr reserviert: Sie können erneut buchen.',
      contact_unavailable:
        'Das Formular kann gerade keine Nachrichten senden: Rufen Sie uns an oder schreiben Sie uns auf WhatsApp.',
      contact_check_failed:
        'Die Spam-Prüfung ist fehlgeschlagen: Versuchen Sie es erneut, oder rufen Sie uns an oder schreiben Sie uns auf WhatsApp.',
      payment_provider_error:
        'Der Zahlungsdienst antwortet nicht. Es wurde nichts abgebucht: Bitte versuchen Sie es gleich noch einmal.',
      signing_required:
        'Diese Ansicht ist nicht mehr aktiv. Bitte geben Sie das Gerät dem Personal zurück.',
      contract_changed:
        'Der Vertrag hat sich inzwischen geändert. Bitte geben Sie das Gerät dem Personal zurück, das ihn Ihnen erneut zeigt.',
      signature_invalid:
        'Die Unterschrift ist nicht lesbar. Löschen Sie sie und unterschreiben Sie erneut.',
    },
  },
  dataExport: {
    fileName: 'LIESMICH.txt',
    title: 'Ihre Daten bei {company}',
    intro:
      'Dieses Archiv enthält die personenbezogenen Daten, die {company} über Sie speichert, erstellt am {date} auf Ihren Antrag vom {requestedOn} ({purpose}).',
    purposes: {
      access: 'Auskunft, Art. 15 DSGVO',
      portability: 'Datenübertragbarkeit, Art. 20 DSGVO',
    },
    contentsTitle: 'Inhalt',
    contents: [
      'data.json: Ihre Daten in maschinenlesbarer Form (JSON, UTF-8).',
      'files/contracts: die von Ihnen unterschriebenen Verträge als PDF.',
      'files/fine-letters: die Schreiben, mit denen wir der Behörde den Fahrer genannt haben, wenn zu einer Ihrer Anmietungen ein Bußgeld kam.',
      'files/documents: die Fotos Ihrer Dokumente, falls Sie sie angefordert haben und wir sie noch aufbewahren.',
    ],
    readingTitle: 'So lesen Sie es',
    reading: [
      'In data.json: customer (Ihre Angaben), licences (Führerscheine), bookings (die Buchungen, jeweils mit Anmietung, Verträgen, Zahlungen, Erstattungen, Kautionen, Steuerbelegen, Zahlungsseiten, Bußgeldern und Zustellungen: wo wir Sie getroffen haben, eine Notiz, die Fähre, mit der Sie ankamen), identities (die bei der Übergabe erfassten Daten), secondDriver (jedes Mal, wenn Sie in der Buchung einer anderen Person als zweiter Fahrer eingetragen wurden: deren Daten, der Zuschlag, das Ergebnis der Prüfung der Voraussetzungen), cargosReports (die Meldungen an die italienische Staatspolizei, CaRGOS), documents (die hochgeladenen Dokumente), legacyRecords (Daten aus unserem früheren System).',
      'Beträge sind in Euro-Cent angegeben: 12345 bedeutet 123,45 €.',
      'Datum und Uhrzeit folgen ISO 8601; Uhrzeiten sind in UTC (die italienische Zeit liegt im Winter eine Stunde, im Sommer zwei Stunden voraus).',
      'Eine Datei mit "path": null ist nicht im Archiv, und "state" sagt, warum: erased (gelöscht), not_requested (nicht angefordert), never_uploaded (nie hochgeladen), unreadable (konnte nicht gelesen werden).',
    ],
    leftOutTitle: 'Nicht enthalten',
    leftOut: [
      'Die Strichdaten Ihrer Unterschrift (Zeit und Druck des Stifts), mit denen man sie nachahmen könnte: Der Vertrag im PDF zeigt die Unterschrift, wie Sie sie geleistet haben.',
      'Fotos und Notizen zum Fahrzeug bei Übergabe und Rückgabe: Sie betreffen das Fahrzeug. Ihnen berechnete Schäden stehen bei ihren Belastungen.',
      'Namen von Mitarbeitern und interne Aufzeichnungen des Unternehmens.',
      'Was Stripe über Kartenzahlungen speichert: Das können Sie bei Stripe anfragen.',
    ],
    contact: 'Bei Fragen zu diesen Daten wenden Sie sich an {company}.',
  },
}
